import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useHistory, useLocation } from 'react-router-dom';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import ProgressLoader from 'components/essentials/ProgressLoader/ProgressLoader';
import CtaButton from 'components/essentials/Cta/CtaButton';
import { GET_MODULAR_SOFA_COLLECTIONS_STATIC } from 'mocks/queries';
import { MODULAR_SOFAS_PAGE_SEO_DESCRIPTION } from 'commons/constants';
import scrollTo from 'commons/scrollTo';
import getVisitorLocationQueryVariables from 'commons/getVisitorLocationQueryVariables';
import useIsBrowser from 'hooks/useIsBrowser';
import useUserLocation from 'global-state/userLocation/useUserLocation';
import ModularSofasCollection from './components/ModularSofasCollection';
import ModularTopNav from './components/ModularTopNav';
import { prepareCollectionDataStatic } from './utils';
import { collectionQuickLinks } from './collectionOrder';

const ModularSofasPage = () => {
  const isBrowser = useIsBrowser();
  const [collectionInView, setCollectionInView] = useState(null);
  const [collectionVisibility, setCollectionVisibility] = useState({});

  const userLocation = useUserLocation();
  const { data: collectionQueryData, loading } = useSuspenseQuery(
    GET_MODULAR_SOFA_COLLECTIONS_STATIC,
    {
      variables: getVisitorLocationQueryVariables(userLocation?.stateShortName),
    }
  );
  const history = useHistory();
  const location = useLocation();
  const collectionRefs = useRef({});

  const isFromCategory =
    location && location.state && location.state.fromPage === 'category';
  const categoryName =
    isFromCategory && location.state.categoryName.label
      ? location.state.categoryName.label
      : '';

  const onTopNavClick = useCallback(collectionId => {
    if (collectionRefs.current[collectionId]) {
      scrollTo({
        left: 0,
        top: collectionRefs.current[collectionId].offsetTop,
        behavior: 'smooth',
      });
      setCollectionInView(collectionId);
    }
  }, []);

  const onCollectionVisible = useCallback(
    collectionId => {
      if (!collectionVisibility[collectionId]) {
        const updatedCollectionVisibility = { ...collectionVisibility };
        updatedCollectionVisibility[collectionId] = true;
        setCollectionVisibility(updatedCollectionVisibility);
      }
      if (collectionId && collectionInView !== collectionId) {
        setCollectionInView(collectionId);
      }
    },
    [collectionInView, collectionVisibility]
  );

  const returnToCategory = useCallback(() => {
    if (isFromCategory) {
      history.go(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFromCategory]);

  const finalCollectionData = useMemo(() => {
    if (collectionQueryData && collectionQueryData.collections) {
      if (!isBrowser) {
        const finalDataSSR = prepareCollectionDataStatic(
          collectionQueryData.collections
        );

        if (finalDataSSR.length) setCollectionInView(finalDataSSR[0].id);

        return finalDataSSR;
      }

      const finalData = prepareCollectionDataStatic(
        collectionQueryData.collections
      );
      const updatedCollectionVisibility = {};
      finalData.forEach((val, index) => {
        collectionRefs.current[val.id] = null;
        updatedCollectionVisibility[val.id] = index < 1; // Only load for first collection
      });
      setCollectionVisibility(updatedCollectionVisibility);

      if (finalData.length) setCollectionInView(finalData[0].id);

      return finalData;
    }
    return null;
  }, [isBrowser, collectionQueryData]);

  const quickLinksData =
    (finalCollectionData?.length &&
      finalCollectionData.filter(collection =>
        collectionQuickLinks.includes(collection.name)
      )) ??
    [];

  if (loading || !finalCollectionData) {
    return (
      <div className="ModularSofasStatic__container">
        <div className="flex flex-col min-h-[30vh] items-center">
          <ProgressLoader />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Modular Sofas, Sectionals, & Couches | Joybird</title>
        <meta name="keywords" content="modular sofa" />
        <meta name="description" content={MODULAR_SOFAS_PAGE_SEO_DESCRIPTION} />
        <meta property="og:type" content="website" />

        <meta
          property="og:description"
          content={MODULAR_SOFAS_PAGE_SEO_DESCRIPTION}
        />

        <meta property="og:url" content="https://joybird.com/modular-sofas/" />
      </Helmet>

      <div className="ModularSofasPage__container">
        {isFromCategory ? (
          <CtaButton
            styling="outline-button"
            size="xs"
            color="brand"
            className="ModularSofasPage__back-category bg-brand-lightest text-sm mt-4 ml-5 lg:ml-7 p-3 !border-none"
            onClick={returnToCategory}
            hasBackArrow
          >
            {'< '} Back to all {categoryName}
          </CtaButton>
        ) : null}

        {finalCollectionData ? (
          <div className="flex flex-col">
            <ModularTopNav
              collectionData={quickLinksData}
              onTopNavClick={onTopNavClick}
              collectionInView={collectionInView}
            />
            {finalCollectionData.map((collection, index) => (
              <ModularSofasCollection
                key={collection.id}
                className={(index + 1) % 2 === 0 ? 'bg-gray-light9' : ''}
                ref={element => {
                  collectionRefs.current[collection.id] = element;
                }}
                collection={collection}
                onCollectionVisible={onCollectionVisible}
                history={history}
                collectionIndex={index}
                collectionVisible={!!collectionVisibility[collection.id]}
              />
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default ModularSofasPage;
